import { type CommonAction, response } from "@iflyrpa/share";
import { z } from "zod";
import { ActionCommonParamsSchema } from "../../types";
import { Http } from "../../utils/http";

const { generateCsrfTokenAdvanced } = require("../../utils/douyin/douyinSafe/csrfToken.js");
const { getABogus } = require("../../utils/douyin/douyinSafe/a_bogus.js");
const { generateDtrait } = require("../../utils/douyin/douyinSafe/dtrait.js");

export const DouyinCheckVerifyQrCodeParamsSchema = ActionCommonParamsSchema.extend({
    token: z.string(), // 二维码 token
    encrypt_uid: z.string(), // decision.encrypt_uid
    verify_scene: z.string().optional(), // decision.event_params.verify_scene
    verify_reason: z.string().optional(), // decision.event_params.verify_reason
    extraParam: z
        .object({
            browserInfo: z.object({
                screen_width: z.string().optional(),
                screen_height: z.string().optional(),
                browser_language: z.string().optional(),
                browser_platform: z.string().optional(),
                browser_name: z.string().optional(),
                "sec-ch-ua-platform": z.string().optional(),
                "sec-ch-ua": z.string().optional(),
                userAgent: z.string().optional(),
            }),
        })
        .optional(),
});

export type DouyinCheckVerifyQrCodeParams = z.infer<typeof DouyinCheckVerifyQrCodeParamsSchema>;

export type QrCodeStatus = "new" | "scanned" | "confirmed" | "expired" | string;

export interface QrCodeStatusNew {
    status: "new";
}

export interface QrCodeStatusConfirmed {
    status: "confirmed";
    redirect_url: string;
    ticket: string;
    cookies: Array<{ name: string; value: string; [key: string]: unknown }>;
}

export interface QrCodeStatusExpired {
    status: "expired";
    token: string;
    qrcode: string;
    qrcode_index_url: string;
    expire_time: number;
    app_name: string;
    web_name: string;
    cookies: Array<{ name: string; value: string; [key: string]: unknown }>;
}

export type QrCodeCheckResult = QrCodeStatusNew | QrCodeStatusConfirmed | QrCodeStatusExpired;

export type DouyinCheckVerifyQrCodeAction = CommonAction<DouyinCheckVerifyQrCodeParams, QrCodeCheckResult>;

/**
 * 解析响应头中的 set-cookie，将新 cookie 合并到旧 cookies 数组中
 */
function mergeSetCookies(
    oldCookies: Array<{ name: string; value: string; [key: string]: unknown }>,
    setCookieHeader: string | string[] | undefined,
): Array<{ name: string; value: string; [key: string]: unknown }> {
    if (!setCookieHeader) return oldCookies;

    const setCookies = Array.isArray(setCookieHeader) ? setCookieHeader : [setCookieHeader];
    const cookieMap = new Map<string, { name: string; value: string; [key: string]: unknown }>(
        oldCookies.map((c) => [c.name, c]),
    );

    for (const raw of setCookies) {
        const parts = raw.split(";");
        const [nameValue] = parts;
        if (!nameValue) continue;
        const eqIdx = nameValue.indexOf("=");
        if (eqIdx === -1) continue;
        const name = nameValue.slice(0, eqIdx).trim();
        const value = nameValue.slice(eqIdx + 1).trim();
        if (!name) continue;

        // 解析其他属性
        const attrs: Record<string, string | boolean> = {};
        for (let i = 1; i < parts.length; i++) {
            const part = parts[i].trim();
            const attrEq = part.indexOf("=");
            if (attrEq === -1) {
                attrs[part.toLowerCase()] = true;
            } else {
                attrs[part.slice(0, attrEq).trim().toLowerCase()] = part.slice(attrEq + 1).trim();
            }
        }

        const existing = cookieMap.get(name);
        cookieMap.set(name, {
            ...(existing || {}),
            ...attrs,
            name,
            value,
        });
    }

    return Array.from(cookieMap.values());
}

export const douyinCheckVerifyQrCode: DouyinCheckVerifyQrCodeAction = async (task, params) => {
    const userAgent =
        params.userAgent ||
        "Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/134.0.0.0 Safari/537.38";

    const bizTraceId = params.cookies.find((e: any) => e.name === "biz_trace_id")?.value || "00000000";

    const msToken = params.cookies.find((e: any) => e.name === "msToken")?.value || "";
    const passportCsrfToken = params.cookies.find((e: any) => e.name === "passport_csrf_token")?.value || "";

    try {
        task.logger.info(`[douyinCheckVerifyQrCode] 开始轮询二维码状态, token=${params.token}`);

        const headers = {
            cookie: params.cookies.map((it: any) => `${it.name}=${it.value}`).join(";"),
            origin: "https://creator.douyin.com",
            referer:
                "https://creator.douyin.com/creator-micro/content/post/image?default-tab=3&enter_from=publish_page&media_type=image&type=new",
            Accept: "application/json, text/plain, */*",
            "Accept-Encoding": "gzip, deflate, br, zstd",
            "sec-ch-ua-platform": params.extraParam?.browserInfo?.["sec-ch-ua-platform"] || "",
            "sec-ch-ua": params.extraParam?.browserInfo?.["sec-ch-ua"] || "",
            "sec-ch-ua-mobile": "?0",
            "user-agent": userAgent,
            "sec-fetch-site": "same-origin",
            "sec-fetch-mode": "cors",
            "sec-fetch-dest": "empty",
            "accept-language": "zh-CN,zh;q=0.9",
            priority: "u=1, i",
        };

        // 构建 POST body
        const checkBody: Record<string, string> = {
            encrypt_uid: params.encrypt_uid || "",
            verify_ticket: "",
            copywriting_key: "creator",
            new_verify_flow: "",
            aid: "2906",
            token: params.token,
            verify_type: "",
            need_record: "1",
            scene: "secondary_verify",
            verify_scene: params.verify_scene || "creator",
            verify_reason: params.verify_reason || "gateway_web_authlv_check",
            std_verify_way: "qr_code_verify",
            new_authn_sdk_version: "1.0.0.404-web",
        };
        const checkBodyStr = new URLSearchParams(checkBody).toString();

        // 构建 URL 查询参数（先不含 a_bogus）
        const checkQueryParams: Record<string, string> = {
            passport_jssdk_version: "5.1.2",
            passport_jssdk_type: "lite",
            is_from_ttaccountsdk: "1",
            aid: "2906",
            language: "zh",
            account_app_language: "zh-CN",
            new_authn_sdk_version: "1.0.0.404-web",
            is_vcd: "1",
            biz_trace_id: bizTraceId,
            msToken,
            a_bogus: "",
        };

        // 生成 a_bogus
        const checkQueryStr = new URLSearchParams(checkQueryParams).toString();
        const checkUrl = `https://creator.douyin.com/passport/mobile/validate_by_qrcode/?${checkQueryStr}`;
        const aBogus = getABogus(checkUrl, checkBodyStr, userAgent);
        checkQueryParams.a_bogus = aBogus;
        const finalQueryStr = new URLSearchParams(checkQueryParams).toString();

        // 生成 CSRF Token 与 sessionDtrait
        const csrfToken = generateCsrfTokenAdvanced({
            cookies: params.cookies,
            url: "https://creator.douyin.com/passport/mobile/validate_by_qrcode/",
            method: "POST",
            userAgent,
        });
        const sessionDtrait = generateDtrait("/passport/mobile/validate_by_qrcode/");

        // 用于捕获响应头中的 set-cookie
        let rawSetCookie: string | string[] | undefined;

        const checkHttp = new Http({
            headers: {
                ...headers,
                "Content-Type": "application/x-www-form-urlencoded",
                "x-secsdk-csrf-token": csrfToken,
                "x-tt-passport-csrf-token": passportCsrfToken,
                "x-tt-passport-trace-id": bizTraceId,
                "x-tt-passport-verify-portrait": "",
                "x-tt-session-dtrait": sessionDtrait || "",
            },
        });

        // 通过拦截器捕获原始响应头
        checkHttp.addResponseInterceptor((res) => {
            task.logger.info(
                `[douyinCheckVerifyQrCode] 原始响应 status=${res.status} data=${JSON.stringify(res.data)}`,
            );
            console.log("[douyinCheckVerifyQrCode] 原始响应:", JSON.stringify(res.data));
            rawSetCookie = res.headers["set-cookie"];
            return undefined;
        });

        const checkResponse = await checkHttp.api<{
            data: {
                status: QrCodeStatus;
                redirect_url?: string;
                ticket?: string;
                token?: string;
                qrcode?: string;
                qrcode_index_url?: string;
                expire_time?: number;
                app_name?: string;
                web_name?: string;
                is_frontier?: boolean;
                [key: string]: unknown;
            };
            message: string;
        }>(
            {
                method: "post",
                url: `https://creator.douyin.com/passport/mobile/validate_by_qrcode/?${finalQueryStr}`,
                data: checkBodyStr,
            },
            { timeout: 10 * 1000 },
        );

        const data = checkResponse?.data;
        const status = data?.status;

        task.logger.info(`[douyinCheckVerifyQrCode] status=${status}`);

        // 合并 set-cookie 到 cookies
        const updatedCookies = mergeSetCookies(params.cookies as any[], rawSetCookie);

        if (status === "new") {
            return response(0, "二维码等待扫描", data);
        }

        if (status === "scanned") {
            return response(0, "二维码已扫码，待确认扫描", data);
        }

        if (status === "confirmed") {
            return response(0, "扫码验证成功", {
                status: "confirmed",
                redirect_url: data.redirect_url || "",
                ticket: data.ticket || "",
                cookies: updatedCookies,
            });
        }

        if (status === "expired") {
            return response(0, "二维码已过期", {
                status: "expired",
                token: data.token || "",
                qrcode: data.qrcode || "",
                qrcode_index_url: data.qrcode_index_url || "",
                expire_time: data.expire_time || 0,
                app_name: data.app_name || "",
                web_name: data.web_name || "",
            });
        }

        return response(414, `未知二维码状态: ${status}`, "" as any);
    } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        task.logger.warn(`[douyinCheckVerifyQrCode] 请求失败: ${msg}`);
        return response(500, `校验二维码失败: ${msg}`, "" as any);
    }
};
